import { ColumnsType } from 'antd/es/table';
import { Employee, EmployeeKpiData } from './types';
import { Button, Space, Tooltip, InputNumber } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { KpiQuestionaire } from './types';

export const getEmployeeStatusColumns = (
  onView: (record: Employee) => void,
  onEdit: (record: Employee) => void,
  onDelete: (record: Employee) => void,
  isActionEnabled: boolean,
  permissions: { isView: boolean; isCreate: boolean; isEdit: boolean; isDelete: boolean }
): ColumnsType<Employee> => [

    {
      title: 'Employee Code',
      dataIndex: 'code',
      key: 'code',
      width: '20%',
    },
    {
      title: 'Employee Name',
      key: 'employeeName',
      width: '50%',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Action',
      key: 'action',

      align: 'center',
      width: '30%',
      render: (_, record) => (
        <div className="flex justify-center  ml-3">
          <Space size={0}>
            {permissions.isView && (
              <Tooltip title="View">
                <span>
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => onView(record)}
                  />
                </span>
              </Tooltip>
            )}
            {permissions.isEdit && (
              <Tooltip title="Edit">
                <span style={{ cursor: isActionEnabled ? 'pointer' : 'not-allowed' }}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => onEdit(record)}
                    disabled={!isActionEnabled}
                  />
                </span>
              </Tooltip>
            )}
            {permissions.isDelete && (
              <Tooltip title="Delete">
                <span style={{ cursor: isActionEnabled ? 'pointer' : 'not-allowed' }}>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => onDelete(record)}
                    disabled={!isActionEnabled}
                  />
                </span>
              </Tooltip>
            )}
          </Space>
        </div>
      ),
    },
  ];

export const getViewKpiDetailsColumns = (): ColumnsType<EmployeeKpiData> => [
  {
    title: 'KRA',
    dataIndex: ['kpiQuestionaire', 'kpiQuestionaire'],
    key: 'kpiQuestion',
    width: '25%',
  },
  {
    title: 'KPI (Detailed)',
    dataIndex: ['kpiQuestionaire', 'kpiDescription'],
    key: 'kpiDescription',
    width: '35%',
    ellipsis: true,
  },
  {
    title: 'Weightage %',
    dataIndex: ['kpiQuestionaire', 'kpiWeightage'],
    key: 'kpiWeightage',
    align: 'center',
    width: '10%',
  },
  {
    title: 'Targets',
    dataIndex: 'totalTargets',
    key: 'totalTargets',
    align: 'center',
    width: '15%',
  },
  {
    title: 'Score %',
    dataIndex: 'weightScore',
    key: 'weightScore',
    align: 'center',
    width: '15%',
  }
];

export const getEmployeeListColumns = (
  onAddKpi: (employee: Employee) => void,
  addedEmployeeIds: number[],
  isActionEnabled: boolean,
  permissions: { isView: boolean; isCreate: boolean; isEdit: boolean; isDelete: boolean }
): ColumnsType<Employee> => [
    {
      title: 'Employee Code',
      dataIndex: 'code',
      key: 'code',
      width: '20%',
    },
    {
      title: 'Employee Name',
      key: 'employeeName',
      width: '50%',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      width: '30%',
      render: (_, record) => (
        addedEmployeeIds.includes(record.employeeId) ? (
          permissions.isView ? (
            <Tooltip title="View ">
              <span>
                <Button type="link" icon={<EyeOutlined style={{ color: '#d34a7c' }} />} onClick={() => onAddKpi(record)} />
              </span>
            </Tooltip>
          ) : null
        ) : (
          permissions.isCreate ? (
            <Tooltip title="Add ">
              <span style={{ cursor: isActionEnabled ? 'pointer' : 'not-allowed' }}>
                <Button type="link" icon={<PlusOutlined />} onClick={() => onAddKpi(record)} disabled={!isActionEnabled} />
              </span>
            </Tooltip>
          ) : null
        )
      ),
    },
  ];

export const getQuestionaireColumns = (
  questionnaireInput: Record<number, { target?: number; actual?: number; weight?: number; }>,
  handleQuestionaireInputChange: (id: number, field: 'target' | 'weight' | 'actual', value: number | null) => void
): ColumnsType<KpiQuestionaire> => [
    {
      title: 'KRA',
      dataIndex: 'kpiQuestionaire',
      key: 'kpiQuestionaire',
      width: '25%',
    },
    {
      title: 'KPI (Detailed)',
      dataIndex: 'kpiDescription',
      key: 'kpiDescription',
      width: '35%',
    },
    {
      title: 'Weightage %',
      dataIndex: 'kpiWeightage',
      key: 'kpiWeightage',
      align: 'center' as const,
      width: '10%',
    },
    {
      title: 'Target',
      key: 'target',
      width: '15%',
      align: 'center' as const,
      render: (_: unknown, record: KpiQuestionaire) => (
        <InputNumber
          placeholder="Enter Target"
          style={{ width: '100%' }}
          controls={false}
          min={0}
          max={999}
          value={questionnaireInput[record.kpiQuestionaireId]?.target}
          onChange={(value) => handleQuestionaireInputChange(record.kpiQuestionaireId, 'target', value)}
          step={0.01}
          precision={0}
          onKeyDown={(event) => {
            if (
              event.key === '.' ||
              (event.key.length === 1 &&
                !/[0-9]/.test(event.key) &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey)
            ) {
              if (event.key === '.') return;
              event.preventDefault();
            }
          }}
        />
      ),
    },
    {
      title: 'Actual',
      key: 'actual',
      width: '15%',
      align: 'center' as const,
      render: (_: unknown, record: KpiQuestionaire) => (
        <InputNumber
          placeholder="Enter Actual"
          style={{ width: '100%' }}
          controls={false}
          min={0}
          precision={0}

          value={questionnaireInput[record.kpiQuestionaireId]?.actual}
          onChange={(value) => handleQuestionaireInputChange(record.kpiQuestionaireId, 'actual', value)}
          step={0.01}
          onKeyDown={(event) => {
            if (
              event.key === '.' ||
              (event.key.length === 1 &&
                !/[0-9]/.test(event.key) &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey)
            ) {
              if (event.key === '.') return;
              event.preventDefault();
            }
          }}
        />
      ),
    },
    {
      title: 'Score %',
      key: 'score',
      align: 'center' as const,
      width: '15%',
      render: (_: unknown, record: KpiQuestionaire) => {
        const input = questionnaireInput[record.kpiQuestionaireId];
        const score =
          input?.target != null &&
            input?.actual != null &&
            input.target !== 0
            ? (input.actual / input.target) * record.kpiWeightage
            : undefined;

        return (
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            value={score}
            precision={0}
            readOnly
            disabled
          />
        );
      },
    }

  ];

export const getEditKpiColumns = (
  editKpiInput: Record<number, { target?: number; weight?: number; actual?: number; kpiId?: number; kpiQuestionaireId?: number; }>,
  handleEditKpiInputChange: (id: number, field: 'target' | 'weight' | 'actual', value: number | null) => void
): ColumnsType<EmployeeKpiData> => [
    {
      title: 'KRA',
      dataIndex: ['kpiQuestionaire', 'kpiQuestionaire'],
      key: 'kpiQuestionaire',
      width: '25%',
    },
    {
      title: 'KPI (Detailed)',
      dataIndex: ['kpiQuestionaire', 'kpiDescription'],
      key: 'kpiDescription',
      width: '35%',
    },
    {
      title: 'Weightage %',
      dataIndex: ['kpiQuestionaire', 'kpiWeightage'],
      key: 'kpiWeightage',
      align: 'center' as const,
      width: '10%',
    },
    {
      title: 'Target',
      key: 'target',
      width: '15%',
      align: 'center' as const,
      render: (_: unknown, record: EmployeeKpiData) => (
        <InputNumber
          placeholder="Enter Target"
          style={{ width: '100%' }}
          controls={false}
          min={0}
          max={999}
          precision={0}

          value={editKpiInput[record.kpiQuestionaire.kpiQuestionaireId]?.target}
          onChange={(value) => handleEditKpiInputChange(record.kpiQuestionaire.kpiQuestionaireId, 'target', value)}
          step={0.01}
          onKeyDown={(event) => {
            if (
              event.key === '.' ||
              (event.key.length === 1 &&
                !/[0-9]/.test(event.key) &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey)
            ) {
              if (event.key === '.') return;
              event.preventDefault();
            }
          }}
        />
      ),
    },
    {
      title: 'Actual',
      key: 'actual',
      width: '15%',
      align: 'center' as const,
      render: (_: unknown, record: EmployeeKpiData) => (
        <InputNumber
          placeholder="Enter Actual"
          style={{ width: '100%' }}
          controls={false}
          min={0}
          precision={0}
          value={editKpiInput[record.kpiQuestionaire.kpiQuestionaireId]?.actual}
          onChange={(value) => handleEditKpiInputChange(record.kpiQuestionaire.kpiQuestionaireId, 'actual', value)}
          step={0.01}
          onKeyDown={(event) => {
            if (
              event.key === '.' ||
              (event.key.length === 1 &&
                !/[0-9]/.test(event.key) &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey)
            ) {
              if (event.key === '.') return;
              event.preventDefault();
            }
          }}
        />
      ),
    },
    {
      title: 'Score %',
      key: 'score',
      align: 'center' as const,
      width: '15%',
      render: (_: unknown, record: EmployeeKpiData) => {
        const input = editKpiInput[record.kpiQuestionaire.kpiQuestionaireId];
        const score =
          input?.target != null &&
            input?.actual != null &&
            input.target !== 0
            ? (input.actual / input.target) * record.kpiQuestionaire.kpiWeightage
            : undefined;
        return (
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            value={score}
            precision={0}
            readOnly
            disabled
          />
        )
      },
    },

  ];
